import type {
  ManyBookingsCar,
  ManyBookingsStation,
} from '@/entities/bookings/many-bookings-booking.entity';
import type { Car, CarStations } from '@/entities/cars/car.entity';
import type { Station } from '@/entities/station/station.entity';
import { type MaybeRefOrGetter, toRef } from '@vueuse/core';
import { computed, type ComputedRef } from 'vue';

type ReturnType<T extends Car | ManyBookingsCar> = T extends Car ? Station : ManyBookingsStation;

const isPrimaryCarStation = (station: { primary?: boolean }) => station.primary !== false;

const primaryStationsOf = (stations: CarStations[]) => stations.filter(isPrimaryCarStation);

const coversDate = (
  range: { fromDate: string | null; untilDate: string | null },
  date: Date,
): boolean => {
  const fromOk = range.fromDate === null || new Date(range.fromDate) <= date;
  const untilOk = range.untilDate === null || new Date(range.untilDate) >= date;
  return fromOk && untilOk;
};

export const useCurrentStationOfCar = <T extends Car | ManyBookingsCar>(
  car: MaybeRefOrGetter<T>,
): ComputedRef<ReturnType<T>> => {
  const carRef = toRef(car);
  return computed(() => getCurrentStationOfCar(carRef.value) as ReturnType<T>);
};

export const useStationOfCarByDate = <T extends Car | ManyBookingsCar>(
  car: MaybeRefOrGetter<T>,
  date: MaybeRefOrGetter<Date | string>,
): ComputedRef<ReturnType<T>> => {
  const carRef = toRef(car);
  const dateRef = toRef(date);
  return computed(() => getStationOfCarByDate(carRef.value, dateRef.value) as ReturnType<T>);
};

export const getCurrentStationOfCar = <T extends Car | ManyBookingsCar>(car: T): ReturnType<T> => {
  return getStationOfCarByDate(car, new Date()) as ReturnType<T>;
};

export const getStationOfCarByDate = <T extends Car | ManyBookingsCar>(
  car: T,
  date: Date | string,
): ReturnType<T> => {
  const stationDate = new Date(date);
  const stations = primaryStationsOf(car.stations as unknown as CarStations[]);
  const coveringPrimary = stations.find((station) => coversDate(station, stationDate));
  if (coveringPrimary) {
    return coveringPrimary.station as ReturnType<T>;
  }
  if (stations.length > 0) {
    return stations.sort((a, b) => {
      const aDate = a.fromDate ? new Date(a.fromDate).getTime() : 0;
      const bDate = b.fromDate ? new Date(b.fromDate).getTime() : 0;
      return aDate > bDate ? 1 : -1;
    })[0].station as ReturnType<T>;
  }
  throw new Error(`Car ${'id' in car ? (car as Car).id : ''} has no primary stations`);
};

export const getLinkedStationsOfCarByDate = <T extends Car | ManyBookingsCar>(
  car: T,
  date: Date | string,
): ReturnType<T>[] => {
  const stationDate = new Date(date);
  const stationsById = new Map<string, ReturnType<T>>();
  for (const row of car.stations as unknown as CarStations[]) {
    if (coversDate(row, stationDate)) {
      stationsById.set(row.station.id, row.station as ReturnType<T>);
    }
  }
  return [...stationsById.values()];
};

export const getOperationalStationOfCar = <T extends Car | ManyBookingsCar>(
  car: T,
  date: Date | string,
  requestedStationIds?: string[] | null,
): ReturnType<T> => {
  const home = getStationOfCarByDate(car, date);
  if (!requestedStationIds?.length) {
    return home;
  }
  const linked = getLinkedStationsOfCarByDate(car, date);
  const requested = new Set(requestedStationIds);
  if (requested.has(home.id) && linked.some((station) => station.id === home.id)) {
    return home;
  }
  const match = linked.find((station) => requested.has(station.id));
  return match ?? home;
};
